import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import axios, { API } from "@/lib/api";
import { useLang, EXC_OPTIONS } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import DatePicker from "@/components/site/DatePicker";

const fieldCls = "w-full bg-transparent border-b border-white/20 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600";
const Label = ({ children }) => <span className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">{children}</span>;

const empty = { date: "", guests: 2, options: [], name: "", email: "", phone: "", message: "" };

const Sent = ({ token, t }) => (
  <div className="gold-frame p-10 sm:p-14 text-center" data-testid="booking-success">
    <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" />
    <h3 className="font-display text-4xl mb-4">{t.sent_title}</h3>
    <p className="text-slate-300 mb-8 max-w-md mx-auto">{t.sent_body}</p>
    <Link to={`/suivi/${token}`} data-testid="track-link" className="inline-block bg-[#D4AF37] text-[#050B14] px-7 py-3.5 tracking-wider hover:bg-[#e6c55a] transition-colors duration-300">{t.sent_track}</Link>
  </div>
);

export default function BookingForm({ excursion, setExcursion }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState(empty);
  const [booked, setBooked] = useState([]);
  const [sending, setSending] = useState(false);
  const [token, setToken] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { axios.get(`${API}/booked-dates`).then((r) => setBooked(r.data)).catch(() => {}); }, []);
  useEffect(() => { setForm((f) => ({ ...f, options: f.options.filter((o) => EXC_OPTIONS[excursion].includes(o)) })); }, [excursion]);

  const toggleOpt = (o) => setForm({ ...form, options: form.options.includes(o) ? form.options.filter((x) => x !== o) : [...form.options, o] });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) return toast.error(t.f_pick_date);
    setSending(true);
    try {
      const r = await axios.post(`${API}/bookings`, { ...form, guests: Number(form.guests), excursion, lang });
      setToken(r.data.token);
    } catch (err) {
      toast.error(err?.response?.status === 409 ? t.f_booked : t.f_error);
    } finally { setSending(false); }
  };

  if (token) return <Sent token={token} t={t} />;

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-10 gap-y-8" data-testid="booking-form">
      <div className="sm:col-span-2">
        <Label>{t.f_excursion}</Label>
        <Select value={excursion} onValueChange={setExcursion}>
          <SelectTrigger data-testid="booking-excursion-select" className="rounded-none bg-transparent border-0 border-b border-white/20 px-0 h-12 text-base focus:ring-0"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A1325] border-white/10 rounded-none">
            {Object.entries(t.exc_names).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`excursion-option-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t.f_date}</Label>
        <DatePicker value={form.date} onChange={(date) => setForm((f) => ({ ...f, date }))} booked={booked} />
      </div>
      <label>
        <Label>{t.f_guests}</Label>
        <input type="number" required min={1} max={20} value={form.guests} onChange={set("guests")} className={fieldCls} data-testid="booking-guests-input" />
      </label>
      {EXC_OPTIONS[excursion].length > 0 && (
        <div className="sm:col-span-2">
          <Label>{t.f_options}</Label>
          <div className="flex flex-wrap gap-3 mt-3">
            {EXC_OPTIONS[excursion].map((o) => (
              <label key={o} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer text-sm transition-colors duration-300 ${form.options.includes(o) ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 hover:border-white/40"}`}>
                <Checkbox checked={form.options.includes(o)} onCheckedChange={() => toggleOpt(o)} data-testid={`option-${o}-checkbox`} className="border-[#D4AF37] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-[#050B14]" />
                {t.options[o]}
              </label>
            ))}
          </div>
        </div>
      )}
      <label><Label>{t.f_name}</Label><input required minLength={2} value={form.name} onChange={set("name")} className={fieldCls} data-testid="booking-name-input" /></label>
      <label><Label>{t.f_email}</Label><input type="email" required value={form.email} onChange={set("email")} className={fieldCls} data-testid="booking-email-input" /></label>
      <label className="sm:col-span-2"><Label>{t.f_phone}</Label><input required minLength={5} value={form.phone} onChange={set("phone")} className={fieldCls} data-testid="booking-phone-input" /></label>
      <label className="sm:col-span-2"><Label>{t.f_message}</Label><textarea rows={3} value={form.message} onChange={set("message")} className={`${fieldCls} resize-none`} data-testid="booking-message-input" /></label>
      <div className="sm:col-span-2">
        <button type="submit" disabled={sending} data-testid="booking-submit-btn"
          className="bg-[#D4AF37] text-[#050B14] px-10 py-4 font-medium tracking-wider hover:bg-[#e6c55a] disabled:opacity-60 transition-colors duration-300 inline-flex items-center gap-2">
          {sending && <Loader2 className="w-4 h-4 animate-spin" />}{sending ? t.f_sending : t.f_submit}
        </button>
      </div>
    </form>
  );
}
